import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Droplet,
  AlertTriangle,
  Users,
  Package,
  BarChart3,
  PieChart,
  Printer,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  CheckCircle2,
  Zap,
  CreditCard,
  Fuel,
  Activity,
  Clock,
  ChevronRight,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  LineChart,
  Line,
} from "recharts";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { formatDate } from "@/lib/dateUtils";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchWithTimeout } from "@/utils/fetchWithTimeout";
import { ActivityCalendar } from "@/components/ActivityCalendar";

export default function Dashboard() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showSimulatedData, setShowSimulatedData] = useState(true);
  const queryClient = useQueryClient();

  // ─── Shared raw data queries (fetched ONCE, derived values computed below) ────
  // Previously, sale-entries and credit-sales were each fetched 2-3 times per cycle
  // by different hooks. Now they are fetched once and shared via useMemo.

  const { data: rawSaleEntries, isLoading: loadingSales, isError: salesError } = useQuery<any[]>({
    queryKey: ["dashboard-raw-sale-entries", selectedDate],
    queryFn: async () => {
      try {
        const res = await fetchWithTimeout(
          `/api/sale-entries?from=${selectedDate}&to=${selectedDate}`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          8000
        );
        if (!res.ok) return [];
        const j = await res.json();
        return (j?.ok || j?.success) ? (j.rows || []) : [];
      } catch { return []; }
    },
    retry: 1,
    staleTime: 60_000,       // Don't refetch if data is fresher than 60s
    refetchInterval: 90_000, // Poll every 90s instead of 30s
  });

  const { data: rawCreditSales } = useQuery<any[]>({
    queryKey: ["dashboard-raw-credit-sales", selectedDate],
    queryFn: async () => {
      try {
        const res = await fetchWithTimeout(
          `/api/credit-sales?from_date=${selectedDate}&to_date=${selectedDate}`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          8000
        );
        if (!res.ok) return [];
        const j = await res.json();
        return j?.ok ? (j.rows || []) : [];
      } catch { return []; }
    },
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 90_000,
  });

  // ─── Derived values (no extra API calls) ─────────────────────────────────────
  const todaySales = useMemo(() => {
    const fuelSale = (rawSaleEntries || []).reduce((sum: number, s: any) => sum + Number(s.net_sale_amount || 0), 0);
    const creditSale = (rawCreditSales || []).reduce((sum: number, s: any) => sum + Number(s.total_amount || 0), 0);
    return { fuelSale, creditSale, discountOffered: 0 };
  }, [rawSaleEntries, rawCreditSales]);

  // Fetch lubricant sales data
  const { data: lubricantSales } = useQuery({
    queryKey: ["dashboard-lubricant-sales", selectedDate],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(
          `/api/lubricant-sales?from_date=${selectedDate}&to_date=${selectedDate}`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          15000
        );
        const result = await response.json();
        const sales = result?.ok ? (result.rows || []) : [];
        return sales.reduce((sum: number, s: any) => sum + Number(s.amount || s.total_amount || 0), 0);
      } catch (error) {
        console.error('Error fetching lubricant sales:', error);
        return 0;
      }
    },
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 90_000,
  });

  // Fetch swipe transactions data
  const { data: swipeData } = useQuery({
    queryKey: ["dashboard-swipes", selectedDate],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(
          `/api/swipe-transactions?from=${selectedDate}&to=${selectedDate}&from_date=${selectedDate}&to_date=${selectedDate}`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          5000
        );
        if (!response.ok) return 0;
        const result = await response.json();
        const swipes = result?.ok ? (result.rows || []) : [];
        return swipes.reduce((sum: number, s: any) => sum + Number(s.amount || s.swipe_amount || 0), 0);
      } catch { return 0; }
    },
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 90_000,
  });

  // Fetch recovery data
  const { data: recoveryData } = useQuery({
    queryKey: ["dashboard-recoveries", selectedDate],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(
          `/api/recoveries?from_date=${selectedDate}&to_date=${selectedDate}`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          15000
        );
        if (!response.ok) return 0;
        const result = await response.json();
        const recoveries = result?.ok ? (result.rows || []) : [];
        return recoveries.reduce((sum: number, r: any) => sum + Number(r.received_amount || 0), 0);
      } catch { return 0; }
    },
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 90_000,
  });

  // Fetch expenses data
  const { data: expensesData } = useQuery({
    queryKey: ["dashboard-expenses", selectedDate],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(
          `/api/expenses?from_date=${selectedDate}&to_date=${selectedDate}`,
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          5000
        );
        const result = await response.json();
        const expenses = result?.ok ? (result.rows || []) : [];
        return expenses.reduce((sum: number, e: any) => sum + Number(e.amount || 0), 0);
      } catch { return 0; }
    },
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 90_000,
  });

  // Day sale flow — derived from shared raw queries (no extra API calls)
  const daySaleFlow = useMemo(() => {
    const cash = (rawSaleEntries || []).reduce((sum: number, s: any) => sum + Number(s.net_sale_amount || 0), 0)
      + (lubricantSales || 0);
    const credit = (rawCreditSales || []).reduce((sum: number, s: any) => sum + Number(s.total_amount || 0), 0);
    const bank = swipeData || 0;
    const vendor = 0;
    return { cash, credit, bank, vendor, saleDisc: 0, total: cash + credit + bank + vendor };
  }, [rawSaleEntries, rawCreditSales, lubricantSales, swipeData]);

  // Fetch day opening cash data (fuel-products, rarely changes — long staleTime)
  const { data: dayOpeningCash } = useQuery({
    queryKey: ["dashboard-day-opening-cash"],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout('/api/fuel-products', { credentials: 'include' }, 5000);
        const result = await response.json();
        return result?.ok ? (result.rows || []) : [];
      } catch { return []; }
    },
    staleTime: 300_000, // Fuel products change very rarely — cache for 5 minutes
    refetchInterval: 300_000,
  });

  // Fetch monthly sales data — 12 months, refetch every 5 min (not 1 min)
  const { data: monthlySales } = useQuery({
    queryKey: ["dashboard-monthly-sales"],
    queryFn: async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 11);

        const [saleRes, creditRes] = await Promise.allSettled([
          fetchWithTimeout(
            `/api/sale-entries?from=${startDate.toISOString().split('T')[0]}&to=${endDate.toISOString().split('T')[0]}`,
            { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
            10000
          ),
          fetchWithTimeout(
            `/api/credit-sales?from_date=${startDate.toISOString().split('T')[0]}&to_date=${endDate.toISOString().split('T')[0]}`,
            { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
            8000
          )
        ]);

        const monthlyData: { [key: string]: number } = {};
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

        if (saleRes.status === 'fulfilled' && saleRes.value.ok) {
          const j = await saleRes.value.json();
          const rows = (j?.ok || j?.success) ? (j.rows || []) : [];
          rows.forEach((s: any) => {
            const dateStr = s.sale_date || '';
            if (!dateStr) return;
            const month = months[new Date(dateStr).getMonth()];
            if (month) monthlyData[month] = (monthlyData[month] || 0) + Number(s.net_sale_amount || 0);
          });
        }
        if (creditRes.status === 'fulfilled' && creditRes.value.ok) {
          const j = await creditRes.value.json();
          const rows = j?.ok ? (j.rows || []) : [];
          rows.forEach((s: any) => {
            const dateStr = s.sale_date || s.saleDate || '';
            if (!dateStr) return;
            const month = months[new Date(dateStr).getMonth()];
            if (month) monthlyData[month] = (monthlyData[month] || 0) + Number(s.total_amount || 0);
          });
        }

        return months.map(month => ({ month, amount: monthlyData[month] || 0 }));
      } catch (error) {
        console.error('Error fetching monthly sales:', error);
        return [];
      }
    },
    staleTime: 120_000,
    refetchInterval: 300_000, // Monthly chart — update every 5 minutes is fine
  });

  // Fetch credit customers outstanding (served from server-side cache after first load)
  const { data: creditCustomers, isLoading: loadingCustomers, isError: customersError } = useQuery({
    queryKey: ["dashboard-credit-customers"],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(
          '/api/credit-customers',
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          5000
        );
        if (!response.ok) throw new Error(`Credit customers API failed: ${response.statusText}`);
        const result = await response.json();
        const customers = result?.ok ? (result.rows || []) : [];
        return customers.filter((c: any) => c?.is_active).slice(0, 10);
      } catch (error) {
        console.error('Error fetching credit customers:', error);
        return [];
      }
    },
    retry: 1,
    staleTime: 120_000,      // Served from server-side cache anyway; 2 min client stale is fine
    refetchInterval: 120_000,
  });

  // Calculate credit outstanding total
  const creditOutstanding = creditCustomers?.reduce((sum: number, c: any) =>
    sum + Number(c.current_balance || 0), 0) || 0;

  // Fetch customer last payments - from recovery records
  const { data: customerPayments } = useQuery({
    queryKey: ["dashboard-customer-payments"],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(
          '/api/recoveries?limit=10',
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          5000
        );
        const result = await response.json();
        const rows = result?.ok ? (result.rows || []) : [];
        return rows.slice(0, 10).map((r: any) => ({
          name: r.organization_name || r.customer_name || r.credit_customer_name || 'Unknown',
          lastPaid: r.recovery_date || r.date || '-',
          due: Number(r.received_amount || r.amount || 0)
        }));
      } catch { return []; }
    },
    staleTime: 90_000,
    refetchInterval: 120_000,
  });

  // Fetch tank stock data
  const { data: tankStock } = useQuery({
    queryKey: ["dashboard-tank-stock"],
    queryFn: async () => {
      try {
        const response = await fetchWithTimeout(
          '/api/tanks',
          { credentials: 'include', headers: { 'Content-Type': 'application/json' } },
          5000
        );
        const result = await response.json();
        const tanks = result?.ok ? (result.rows || []) : [];
        return tanks.map((t: any) => ({
          tankName: t.tankNumber || t.tank_number || t.tank_name || 'Tank',
          currentStock: Number(t.currentStock || t.current_stock || 0),
          productName: t.product_name || t.productName || '',
          capacity: Number(t.capacity || t.tankCapacity || 0),
        }));
      } catch { return []; }
    },
    staleTime: 90_000,
    refetchInterval: 120_000,
  });

  // Fetch credit parties data (monthly credit sales vs recoveries)
  const { data: creditParties } = useQuery({
    queryKey: ["dashboard-credit-parties"],
    queryFn: async () => {
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 5);
        const from = startDate.toISOString().split('T')[0];
        const to = endDate.toISOString().split('T')[0];
        const [creditRes, recoveryRes] = await Promise.allSettled([
          fetchWithTimeout(`/api/credit-sales?from_date=${from}&to_date=${to}`, { credentials: 'include' }, 8000),
          fetchWithTimeout(`/api/recoveries?from_date=${from}&to_date=${to}`, { credentials: 'include' }, 8000),
        ]);
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const creditByMonth: Record<string, number> = {};
        const recoveryByMonth: Record<string, number> = {};
        if (creditRes.status === 'fulfilled' && creditRes.value.ok) {
          const j = await creditRes.value.json();
          (j.rows || []).forEach((r: any) => {
            const m = months[new Date(r.sale_date || r.date || r.saleDate || '').getMonth()];
            if (m) creditByMonth[m] = (creditByMonth[m] || 0) + Number(r.total_amount || 0);
          });
        }
        if (recoveryRes.status === 'fulfilled' && recoveryRes.value.ok) {
          const j = await recoveryRes.value.json();
          (j.rows || []).forEach((r: any) => {
            const m = months[new Date(r.recovery_date || r.date || '').getMonth()];
            if (m) recoveryByMonth[m] = (recoveryByMonth[m] || 0) + Number(r.received_amount || r.amount || 0);
          });
        }

        const last6Months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date();
          d.setMonth(d.getMonth() - i);
          const m = months[d.getMonth()];
          last6Months.push({
            month: m,
            creditAmount: creditByMonth[m] || 0,
            recovery: recoveryByMonth[m] || 0,
          });
        }
        return last6Months;
      } catch (error) {
        console.error('Error fetching credit parties:', error);
        return [];
      }
    },
    staleTime: 120_000,
    refetchInterval: 300_000, // 6-month chart — update every 5 min is plenty
  });



  const COLORS = ["#6366f1", "#f59e0b", "#14b8a6", "#ec4899"];





  // Mock sparkline data generator for nice stats card visual style
  const generateSparklineData = (baseVal: number, isGhost: boolean = false) => {
    const val = (baseVal === 0 || isGhost) ? 100 : baseVal;
    return [
      { pv: val * 0.7 },
      { pv: val * 0.95 },
      { pv: val * 0.8 },
      { pv: val * 1.15 },
      { pv: val * 1.0 },
    ];
  };

  const handleRefreshAll = () => {
    queryClient.invalidateQueries();
  };

  const fuelAndLubSales = ((todaySales?.fuelSale || 0) + (lubricantSales || 0));
  const creditSales = (todaySales?.creditSale || 0);
  const totalSwipes = (swipeData || 0);
  const bunkRecoveries = (recoveryData || 0);
  const totalExpenses = (expensesData || 0);

  // Check if live data is completely empty for the selected date
  const isLiveDataEmpty =
    fuelAndLubSales === 0 &&
    creditSales === 0 &&
    totalSwipes === 0 &&
    bunkRecoveries === 0 &&
    totalExpenses === 0;

  const isDemoActive = isLiveDataEmpty && showSimulatedData;

  // Curated mock simulation values to showcase gorgeous layout
  const displayFuelAndLub = isDemoActive ? 148500 : fuelAndLubSales;
  const displayCredit = isDemoActive ? 42600 : creditSales;
  const displaySwipes = isDemoActive ? 38400 : totalSwipes;
  const displayRecoveries = isDemoActive ? 29000 : bunkRecoveries;
  const displayExpenses = isDemoActive ? 9200 : totalExpenses;

  // Ghost checks: If it is completely empty and simulation is toggled off
  const isPieChartEmpty = !isDemoActive && (!daySaleFlow || daySaleFlow.total === 0);
  const isBankFlowEmpty = !isDemoActive && (!daySaleFlow || daySaleFlow.bank === 0);
  const isMonthlySalesEmpty = !isDemoActive && (!monthlySales || monthlySales.length === 0);
  const isLedgerEmpty = !isDemoActive && isLiveDataEmpty;
  const isTankStockEmpty = !isDemoActive && (!tankStock || tankStock.length === 0 || tankStock.every((t: any) => t.currentStock === 0));
  const isCreditPartiesEmpty = !isDemoActive && (!creditParties || creditParties.length === 0);

  // Day Sale Flow setup
  const displaySaleFlow = isDemoActive 
    ? { cash: 67500, credit: 42600, bank: 38400, vendor: 0, total: 148500 }
    : (isPieChartEmpty ? { cash: 0, credit: 0, bank: 0, vendor: 0, total: 0 } : daySaleFlow);

  // Ghost-aware datasets with grey outline placeholder rows when completely empty
  const hasRealCreditCustomers = creditCustomers && creditCustomers.length > 0;
  const displayCreditCustomers = isDemoActive ? [
    { name: "Reliance Logistics Ltd", mobile: "+91 98765 43210", credit_limit: 500000, current_balance: 185000, organization_name: "Reliance Logistics Ltd" },
    { name: "Sharma Transport Co", mobile: "+91 99112 23344", credit_limit: 300000, current_balance: 124000, organization_name: "Sharma Transport Co" },
    { name: "Gita Fuel Carriers", mobile: "+91 98123 45678", credit_limit: 200000, current_balance: 95000, organization_name: "Gita Fuel Carriers" },
    { name: "Balaji Freight Corp", mobile: "+91 90011 22334", credit_limit: 400000, current_balance: 78000, organization_name: "Balaji Freight Corp" }
  ] : (hasRealCreditCustomers ? creditCustomers : [
    { name: "Placeholder Transport Co", mobile: "+91 ••••• •••••", credit_limit: 250000, current_balance: 75000, isGhost: true },
    { name: "Placeholder Logistics Ltd", mobile: "+91 ••••• •••••", credit_limit: 180000, current_balance: 45000, isGhost: true },
    { name: "Placeholder Fuel Carrier", mobile: "+91 ••••• •••••", credit_limit: 300000, current_balance: 90000, isGhost: true }
  ]);

  const hasRealCustomerPayments = customerPayments && customerPayments.length > 0;
  const displayCustomerPayments = isDemoActive ? [
    { name: "Reliance Logistics Ltd", lastPaid: new Date().toISOString(), due: 50000 },
    { name: "Sharma Transport Co", lastPaid: new Date().toISOString(), due: 35000 },
    { name: "Gita Fuel Carriers", lastPaid: new Date().toISOString(), due: 20000 }
  ] : (hasRealCustomerPayments ? customerPayments : [
    { name: "Placeholder Transport Co", lastPaid: new Date().toISOString(), due: 25000, isGhost: true },
    { name: "Placeholder Logistics Ltd", lastPaid: new Date().toISOString(), due: 15000, isGhost: true },
    { name: "Placeholder Fuel Carrier", lastPaid: new Date().toISOString(), due: 10000, isGhost: true }
  ]);

  const displayTankStock: any[] = isDemoActive 
    ? [
        { tankName: "Tank 1", currentStock: 14500, productName: "XP", capacity: 25000 },
        { tankName: "Tank 2", currentStock: 18000, productName: "HSD", capacity: 30000 },
        { tankName: "Tank 3", currentStock: 12000, productName: "MS", capacity: 20000 }
      ]
    : (isTankStockEmpty 
        ? [
            { tankName: "Tank 1", currentStock: 0, productName: "-", capacity: 25000, isGhost: true },
            { tankName: "Tank 2", currentStock: 0, productName: "-", capacity: 25000, isGhost: true },
            { tankName: "Tank 3", currentStock: 0, productName: "-", capacity: 25000, isGhost: true }
          ]
        : (tankStock || []));

  const displayMonthlySales = isDemoActive 
    ? [
        { month: "Jan", amount: 120000 },
        { month: "Feb", amount: 145000 },
        { month: "Mar", amount: 160000 },
        { month: "Apr", amount: 135000 },
        { month: "May", amount: 180000 },
        { month: "Jun", amount: 195000 },
        { month: "Jul", amount: 210000 },
        { month: "Aug", amount: 175000 },
        { month: "Sep", amount: 190000 },
        { month: "Oct", amount: 225000 },
        { month: "Nov", amount: 240000 },
        { month: "Dec", amount: 260000 }
      ]
    : (isMonthlySalesEmpty
        ? [
            { month: "Jan", amount: 25000, isGhost: true },
            { month: "Feb", amount: 25000, isGhost: true },
            { month: "Mar", amount: 25000, isGhost: true },
            { month: "Apr", amount: 25000, isGhost: true },
            { month: "May", amount: 25000, isGhost: true },
            { month: "Jun", amount: 25000, isGhost: true },
            { month: "Jul", amount: 25000, isGhost: true },
            { month: "Aug", amount: 25000, isGhost: true },
            { month: "Sep", amount: 25000, isGhost: true },
            { month: "Oct", amount: 25000, isGhost: true },
            { month: "Nov", amount: 25000, isGhost: true },
            { month: "Dec", amount: 25000, isGhost: true }
          ]
        : monthlySales);

  const displayCreditParties = isDemoActive 
    ? [
        { month: "Jan", creditAmount: 85000, recovery: 72000 },
        { month: "Feb", creditAmount: 95000, recovery: 88000 },
        { month: "Mar", creditAmount: 110000, recovery: 95000 },
        { month: "Apr", creditAmount: 105000, recovery: 100000 },
        { month: "May", creditAmount: 125000, recovery: 115000 },
        { month: "Jun", creditAmount: 140000, recovery: 130000 }
      ]
    : (isCreditPartiesEmpty
        ? [
            { month: "Jan", creditAmount: 15000, recovery: 10000, isGhost: true },
            { month: "Feb", creditAmount: 15000, recovery: 10000, isGhost: true },
            { month: "Mar", creditAmount: 15000, recovery: 10000, isGhost: true },
            { month: "Apr", creditAmount: 15000, recovery: 10000, isGhost: true },
            { month: "May", creditAmount: 15000, recovery: 10000, isGhost: true },
            { month: "Jun", creditAmount: 15000, recovery: 10000, isGhost: true }
          ]
        : creditParties);

  const displayLedgerData = isLedgerEmpty
    ? [
        { name: "Sale Amount", value: 6000, isGhost: true },
        { name: "Swipes", value: 6000, isGhost: true },
        { name: "Recoveries", value: 6000, isGhost: true },
        { name: "Credits", value: 6000, isGhost: true },
        { name: "Expenses", value: 6000, isGhost: true }
      ]
    : [
        { name: "Sale Amount", value: displayFuelAndLub + displayCredit },
        { name: "Swipes", value: displaySwipes },
        { name: "Recoveries", value: displayRecoveries },
        { name: "Credits", value: displayCredit },
        { name: "Expenses", value: displayExpenses }
      ];

  return (
    <div className="space-y-8 p-6 lg:p-8 bg-slate-50/50 min-h-screen text-slate-900 font-sans">
      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm transition-all duration-300">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 bg-clip-text text-transparent">
              Operations Hub
            </h1>
            <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200/50 flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              Live & Multi-Tenant Verified
            </Badge>
          </div>
          <p className="text-sm text-slate-500 font-medium">
            PetroOne real-time bunk statistics, fuel sales & credit collections.
          </p>
        </div>

        {/* Date Filter & Control Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <ActivityCalendar value={selectedDate} onChange={setSelectedDate} />

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            className="h-10 bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-300 text-slate-700 font-semibold px-4 rounded-xl shadow-sm flex items-center gap-2 transition-all duration-300 active:scale-95 group"
          >
            <RefreshCw className="h-4 w-4 text-slate-500 group-hover:rotate-180 transition-transform duration-500" />
            Sync Now
          </Button>
        </div>
      </div>

      {/* Simulation / Preview Banner */}
      {isDemoActive && (
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl p-4 text-white shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4 border border-indigo-500/30 animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 text-yellow-300 rounded-xl shadow-inner">
              <Zap className="h-5 w-5 animate-pulse" />
            </div>
            <div>
              <p className="font-bold text-sm flex items-center gap-1.5">
                <span>Simulation Preview Active</span>
                <Badge className="bg-white/20 hover:bg-white/20 text-white text-[10px] py-0.5 rounded-md border-0 font-bold tracking-wider">PREVIEW</Badge>
              </p>
              <p className="text-xs text-indigo-100 mt-1">
                No active sales drop records were found for <strong className="text-white bg-white/20 px-1.5 py-0.5 rounded">{selectedDate}</strong>. Curated bunk figures are loaded for visual fidelity.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowSimulatedData(false)}
            className="bg-white hover:bg-slate-100 text-indigo-700 font-extrabold text-xs px-4 py-2 rounded-xl transition-all shadow-md shrink-0 active:scale-95 border-0"
          >
            Show Live Empty State
          </Button>
        </div>
      )}

      {!isDemoActive && isLiveDataEmpty && (
        <div className="bg-amber-50 border border-amber-200/80 rounded-2xl p-4 text-amber-900 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 border-l-4 border-l-amber-500 animate-in fade-in duration-300">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 text-amber-700 rounded-xl">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-sm">No Live Sales Logged for Selected Date</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Daily reports, expenses, card collections and shift logs are currently empty for this bunk date.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setShowSimulatedData(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white font-extrabold text-xs px-4 py-2 rounded-xl transition-all shadow-md shrink-0 active:scale-95 border-0"
          >
            Load Simulated Demo Data
          </Button>
        </div>
      )}

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Card 1: Fuel & Lubricant Sales */}
        <Card className="relative overflow-hidden bg-white border border-slate-200 hover:border-indigo-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group rounded-2xl hover:-translate-y-1">
          <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-indigo-500 to-indigo-600" />
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Fuel & Lubs Sales</span>
              <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                <Fuel className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-black tracking-tight text-slate-900">
                ₹{displayFuelAndLub.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs font-semibold text-slate-400">Total Bunk Retail Sales</p>
            </div>
            {/* Sparkline mini-chart */}
            <div className="h-10 w-full opacity-80 group-hover:opacity-100 transition-opacity">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateSparklineData(displayFuelAndLub, isLiveDataEmpty && !isDemoActive)}>
                  <defs>
                    <linearGradient id="sparkline-indigo" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="sparkline-ghost-1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.25}/>
                      <stop offset="100%" stopColor="#94a3b8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area
                    dataKey="pv"
                    stroke={isLiveDataEmpty && !isDemoActive ? "#cbd5e1" : "#6366f1"}
                    strokeDasharray={isLiveDataEmpty && !isDemoActive ? "3 3" : undefined}
                    strokeWidth={1.5}
                    fillOpacity={1}
                    fill={isLiveDataEmpty && !isDemoActive ? "url(#sparkline-ghost-1)" : "url(#sparkline-indigo)"}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 px-2 py-0.5 border border-indigo-200/50 rounded-full font-bold flex items-center gap-0.5 shadow-sm">
                <TrendingUp className="h-3 w-3" />
                +4.8%
              </Badge>
              <span className="text-slate-400 font-medium">vs last month</span>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Credit Sales */}
        <Card className="relative overflow-hidden bg-white border border-slate-200 hover:border-amber-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group rounded-2xl hover:-translate-y-1">
          <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-amber-500 to-amber-600" />
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Credit Sales</span>
              <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl group-hover:bg-amber-600 group-hover:text-white transition-all duration-300">
                <Users className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-black tracking-tight text-slate-900">
                ₹{displayCredit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs font-semibold text-slate-400">Total Credit Disbursed</p>
            </div>
            {/* Sparkline mini-chart */}
            <div className="h-10 w-full opacity-80 group-hover:opacity-100 transition-opacity">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateSparklineData(displayCredit, isLiveDataEmpty && !isDemoActive)}>
                  <defs>
                    <linearGradient id="sparkline-amber" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="sparkline-ghost-2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.25}/>
                      <stop offset="100%" stopColor="#94a3b8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area
                    dataKey="pv"
                    stroke={isLiveDataEmpty && !isDemoActive ? "#cbd5e1" : "#f59e0b"}
                    strokeDasharray={isLiveDataEmpty && !isDemoActive ? "3 3" : undefined}
                    strokeWidth={1.5}
                    fillOpacity={1}
                    fill={isLiveDataEmpty && !isDemoActive ? "url(#sparkline-ghost-2)" : "url(#sparkline-amber)"}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 px-2 py-0.5 border border-amber-200/50 rounded-full font-bold flex items-center gap-0.5 shadow-sm">
                <Activity className="h-3 w-3" />
                Active Ledger
              </Badge>
              <span className="text-slate-400 font-medium">Bunk credit limit status</span>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Swipe Machines */}
        <Card className="relative overflow-hidden bg-white border border-slate-200 hover:border-teal-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group rounded-2xl hover:-translate-y-1">
          <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-teal-500 to-teal-600" />
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Swipe Collections</span>
              <div className="p-2.5 bg-teal-50 text-teal-600 rounded-xl group-hover:bg-teal-600 group-hover:text-white transition-all duration-300">
                <CreditCard className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-black tracking-tight text-slate-900">
                ₹{displaySwipes.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs font-semibold text-slate-400">POS Machine Terminals</p>
            </div>
            {/* Sparkline mini-chart */}
            <div className="h-10 w-full opacity-80 group-hover:opacity-100 transition-opacity">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateSparklineData(displaySwipes, isLiveDataEmpty && !isDemoActive)}>
                  <defs>
                    <linearGradient id="sparkline-teal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#14b8a6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="sparkline-ghost-3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.25}/>
                      <stop offset="100%" stopColor="#94a3b8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area
                    dataKey="pv"
                    stroke={isLiveDataEmpty && !isDemoActive ? "#cbd5e1" : "#14b8a6"}
                    strokeDasharray={isLiveDataEmpty && !isDemoActive ? "3 3" : undefined}
                    strokeWidth={1.5}
                    fillOpacity={1}
                    fill={isLiveDataEmpty && !isDemoActive ? "url(#sparkline-ghost-3)" : "url(#sparkline-teal)"}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 px-2 py-0.5 border border-emerald-200/50 rounded-full font-bold flex items-center gap-0.5 shadow-sm">
                <TrendingUp className="h-3 w-3" />
                +2.1%
              </Badge>
              <span className="text-slate-400 font-medium">vs yesterday</span>
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Credit Recoveries */}
        <Card className="relative overflow-hidden bg-white border border-slate-200 hover:border-rose-200/80 shadow-sm hover:shadow-xl transition-all duration-300 group rounded-2xl hover:-translate-y-1">
          <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-rose-500 to-rose-600" />
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Credit Recoveries</span>
              <div className="p-2.5 bg-rose-50 text-rose-600 rounded-xl group-hover:bg-rose-600 group-hover:text-white transition-all duration-300">
                <DollarSign className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-black tracking-tight text-slate-900">
                ₹{displayRecoveries.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs font-semibold text-slate-400">Recovered Outstanding Cash</p>
            </div>
            {/* Sparkline mini-chart */}
            <div className="h-10 w-full opacity-80 group-hover:opacity-100 transition-opacity">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateSparklineData(displayRecoveries, isLiveDataEmpty && !isDemoActive)}>
                  <defs>
                    <linearGradient id="sparkline-rose" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#f43f5e" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="sparkline-ghost-4" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.25}/>
                      <stop offset="100%" stopColor="#94a3b8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area
                    dataKey="pv"
                    stroke={isLiveDataEmpty && !isDemoActive ? "#cbd5e1" : "#f43f5e"}
                    strokeDasharray={isLiveDataEmpty && !isDemoActive ? "3 3" : undefined}
                    strokeWidth={1.5}
                    fillOpacity={1}
                    fill={isLiveDataEmpty && !isDemoActive ? "url(#sparkline-ghost-4)" : "url(#sparkline-rose)"}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 px-2 py-0.5 border border-emerald-200/50 rounded-full font-bold flex items-center gap-0.5 shadow-sm">
                <CheckCircle2 className="h-3 w-3" />
                Successful
              </Badge>
              <span className="text-slate-400 font-medium">Bunk cash inflows</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dynamic Bunk Operation Warnings / Alerts */}
      <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border border-amber-200/60 rounded-2xl p-5 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-100 text-amber-700 rounded-xl mt-0.5">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-amber-900 text-sm">Low Stock Alert Drawer</h3>
            <p className="text-xs text-amber-700/90 font-medium mt-0.5">
              Some lubricant products are currently trending below their minimum reorder thresholds. Verify stock reconciliations on the Minimum Stock page.
            </p>
          </div>
        </div>
        <Button
          onClick={() => window.location.href = "/product-stock/minimum-stock"}
          className="bg-amber-600 hover:bg-amber-700 text-white border-0 text-xs font-bold px-4 py-2 rounded-xl transition-all shadow-sm shrink-0 flex items-center gap-1 w-fit"
        >
          Check Minimum Stock <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Charts Section 1: Sales Flow & Pie Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Day Sale Flow Pie Chart */}
        <Card className="lg:col-span-2 rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-indigo-500" />
                  Day Sales Flow Breakdown
                  {isPieChartEmpty && <span className="text-[10px] text-slate-400 font-bold bg-slate-100 px-2 py-0.5 rounded ml-2">Empty State Outline</span>}
                </CardTitle>
                <CardDescription className="text-xs text-slate-500 mt-0.5">
                  Proportional sales methods overview (Cash, Credit, Cards/UPI, and Vendor)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-center gap-8">
            <div className="w-full h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={isPieChartEmpty ? [{ name: "Bunk Idle", value: 1 }] : [
                      { name: "Cash", value: displaySaleFlow?.cash || 0 },
                      { name: "Credit", value: displaySaleFlow?.credit || 0 },
                      { name: "Bank", value: displaySaleFlow?.bank || 0 },
                      { name: "Vendor", value: displaySaleFlow?.vendor || 0 }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    paddingAngle={isPieChartEmpty ? 0 : 4}
                    label={isPieChartEmpty ? undefined : ({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                    dataKey="value"
                  >
                    {isPieChartEmpty ? (
                      <Cell fill="#f8fafc" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="4 4" />
                    ) : (
                      [0, 1, 2, 3].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))
                    )}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#1e293b", color: "#f8fafc", borderRadius: "12px", border: "0", fontSize: "12px" }}
                    itemStyle={{ color: "#f8fafc" }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>

            {/* Custom Interactive Legend */}
            <div className="flex flex-col gap-3 min-w-[160px] w-full sm:w-auto opacity-75">
              {[
                { name: "Cash Sales", val: displaySaleFlow?.cash || 0, color: isPieChartEmpty ? "bg-slate-300" : "bg-indigo-500" },
                { name: "Credit Sales", val: displaySaleFlow?.credit || 0, color: isPieChartEmpty ? "bg-slate-300" : "bg-amber-500" },
                { name: "Bank/Card", val: displaySaleFlow?.bank || 0, color: isPieChartEmpty ? "bg-slate-300" : "bg-teal-500" },
                { name: "Vendor", val: displaySaleFlow?.vendor || 0, color: isPieChartEmpty ? "bg-slate-300" : "bg-pink-500" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-between gap-4 p-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-semibold">
                  <div className="flex items-center gap-2 text-slate-600">
                    <div className={`w-3 h-3 ${item.color} rounded-full`} />
                    <span>{item.name}</span>
                  </div>
                  <span className="text-slate-800 font-extrabold">₹{item.val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Day Bank Flow Pie Chart */}
        <Card className="rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Zap className="h-4 w-4 text-teal-500" />
              Day Bank Flow
            </CardTitle>
            <CardDescription className="text-xs text-slate-500 mt-0.5">
              Specific bank account flow deposits
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <div className="w-full h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={isBankFlowEmpty ? [{ name: "No Inflow", value: 1 }] : [{ name: "PNB CURRENT", value: displaySaleFlow?.bank || 0 }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={0}
                    label={isBankFlowEmpty ? undefined : ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    dataKey="value"
                  >
                    {isBankFlowEmpty ? (
                      <Cell fill="#f8fafc" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="4 4" />
                    ) : (
                      <Cell fill="#14b8a6" />
                    )}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#1e293b", color: "#f8fafc", borderRadius: "12px", border: "0", fontSize: "12px" }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 p-3 bg-teal-50/50 border border-teal-100 rounded-xl w-full text-center">
              <span className="text-[11px] font-bold text-teal-700 uppercase tracking-widest block">PNB Current Bal Inflow</span>
              <span className="text-xl font-black text-teal-800 mt-1 block">
                ₹{(displaySaleFlow?.bank || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section 2: Monthly Sales & Day Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Sale Chart */}
        <Card className="rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-amber-500" />
              Monthly Bunk Sales performance
              {isMonthlySalesEmpty && <span className="text-[10px] text-slate-400 font-bold bg-slate-100 px-2 py-0.5 rounded ml-2">Empty State Outline</span>}
            </CardTitle>
            <CardDescription className="text-xs text-slate-500 mt-0.5">
              Bunk monthly cumulative sales record over past 12 months
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={displayMonthlySales}>
                  <defs>
                    <linearGradient id="monthlySalesGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.9}/>
                      <stop offset="100%" stopColor="#e11d48" stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tickLine={false} tick={{ fill: "#64748b", fontSize: 11, fontWeight: 600 }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                  <Tooltip
                    cursor={{ fill: "rgba(226,232,240,0.3)" }}
                    contentStyle={{ background: "#1e293b", color: "#f8fafc", borderRadius: "12px", border: "0", fontSize: "12px" }}
                  />
                  <Bar dataKey="amount" radius={[6, 6, 0, 0]} name="Total Sale (₹)">
                    {displayMonthlySales.map((entry: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.isGhost ? "#f8fafc" : "url(#monthlySalesGrad)"}
                        stroke={entry.isGhost ? "#cbd5e1" : undefined}
                        strokeWidth={entry.isGhost ? 1.5 : undefined}
                        strokeDasharray={entry.isGhost ? "3 3" : undefined}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Day Summary Ledger Chart */}
        <Card className="rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500" />
              Day Ledger Summary
              {isLedgerEmpty && <span className="text-[10px] text-slate-400 font-bold bg-slate-100 px-2 py-0.5 rounded ml-2">Empty State Outline</span>}
            </CardTitle>
            <CardDescription className="text-xs text-slate-500 mt-0.5">
              Comparative ledger elements metrics for chosen date
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={displayLedgerData} layout="vertical">
                  <defs>
                    <linearGradient id="ledgerGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.9}/>
                      <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={90} tickLine={false} tick={{ fill: "#64748b", fontSize: 10, fontWeight: 700 }} />
                  <Tooltip
                    contentStyle={{ background: "#1e293b", color: "#f8fafc", borderRadius: "12px", border: "0", fontSize: "12px" }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} name="Ledger Value (₹)">
                    {displayLedgerData.map((entry: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.isGhost ? "#f8fafc" : "url(#ledgerGrad)"}
                        stroke={entry.isGhost ? "#cbd5e1" : undefined}
                        strokeWidth={entry.isGhost ? 1.5 : undefined}
                        strokeDasharray={entry.isGhost ? "3 3" : undefined}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grid of Credit Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Credit Customer Outstanding */}
        <Card className="rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-500" />
                Credit Customer Outstanding
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-0.5">
                Top outstanding credit parties currently active
              </CardDescription>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Total Outstanding</span>
              <span className="text-lg font-black text-slate-900 mt-0.5 block">
                ₹{displayCreditCustomers?.reduce((sum: number, c: any) => sum + (c.isGhost ? 0 : Number(c.current_balance || 0)), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 pb-2">
                <span>Party Name</span>
                <span>Mobile</span>
                <span>Credit Limit</span>
                <span className="text-right">Outstanding</span>
              </div>

              {displayCreditCustomers && displayCreditCustomers.length > 0 ? (
                displayCreditCustomers.map((customer: any, index: number) => {
                  const nameStr = customer.organization_name || customer.customer_name || customer.name || 'Unknown';
                  const initials = nameStr.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                  return (
                    <div key={index} className={`grid grid-cols-4 gap-2 text-xs items-center hover:bg-slate-50 p-1.5 rounded-lg transition-colors ${customer.isGhost ? 'opacity-40 select-none' : ''}`}>
                      <span className={`flex items-center gap-2.5 font-bold ${customer.isGhost ? 'text-slate-400' : 'text-slate-700'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${customer.isGhost ? 'bg-slate-100 border border-slate-200 text-slate-400' : 'bg-indigo-50 border border-indigo-100 text-indigo-600'}`}>
                          {initials}
                        </div>
                        <span className="truncate flex items-center gap-1.5">
                          {nameStr}
                          {customer.isGhost && (
                            <Badge className="bg-slate-100 text-slate-400 border border-slate-200/50 hover:bg-slate-100 text-[8px] px-1 py-0 rounded font-bold shadow-none">
                              OUTLINE
                            </Badge>
                          )}
                        </span>
                      </span>
                      <span className={`${customer.isGhost ? 'text-slate-300' : 'text-slate-500'} font-semibold`}>{customer.mobile_number || customer.mobile || '-'}</span>
                      <span className={`${customer.isGhost ? 'text-slate-300' : 'text-slate-500'} font-semibold`}>₹{(customer.credit_limit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className={`text-right font-black ${customer.isGhost ? 'text-slate-400' : 'text-rose-600'}`}>₹{(customer.current_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center text-slate-400 text-xs font-medium border border-dashed border-slate-100 rounded-xl bg-slate-50/50 p-6 flex flex-col items-center justify-center gap-2">
                  <Users className="h-6 w-6 opacity-40 text-slate-400" />
                  <span>No outstanding credit customers registered.</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer Last Payment / recoveries */}
        <Card className="rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Customer Last Payment
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 mt-0.5">
                Recent outstanding recoveries received
              </CardDescription>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Total Recovered</span>
              <span className="text-lg font-black text-emerald-600 mt-0.5 block">
                ₹{displayRecoveries.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 pb-2">
                <span>Party Name</span>
                <span>Date Received</span>
                <span className="text-right">Amount</span>
              </div>

              {displayCustomerPayments && displayCustomerPayments.length > 0 ? (
                displayCustomerPayments.map((customer: any, index: number) => {
                  const nameStr = customer.name || 'Unknown';
                  const initials = nameStr.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                  const recoveryDate = customer.lastPaid !== '-' ? formatDate(customer.lastPaid) : '-';
                  return (
                    <div key={index} className={`grid grid-cols-3 gap-2 text-xs items-center hover:bg-slate-50 p-1.5 rounded-lg transition-colors ${customer.isGhost ? 'opacity-40 select-none' : ''}`}>
                      <span className={`flex items-center gap-2.5 font-bold ${customer.isGhost ? 'text-slate-400' : 'text-slate-700'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${customer.isGhost ? 'bg-slate-100 border border-slate-200 text-slate-400' : 'bg-emerald-50 border border-emerald-100 text-emerald-600'}`}>
                          {initials}
                        </div>
                        <span className="truncate flex items-center gap-1.5">
                          {nameStr}
                          {customer.isGhost && (
                            <Badge className="bg-slate-100 text-slate-400 border border-slate-200/50 hover:bg-slate-100 text-[8px] px-1 py-0 rounded font-bold shadow-none">
                              OUTLINE
                            </Badge>
                          )}
                        </span>
                      </span>
                      <span className={`${customer.isGhost ? 'text-slate-300' : 'text-slate-500'} font-semibold`}>{recoveryDate}</span>
                      <span className={`text-right font-black ${customer.isGhost ? 'text-slate-400' : 'text-emerald-600'}`}>₹{customer.due.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center text-slate-400 text-xs font-medium border border-dashed border-slate-100 rounded-xl bg-slate-50/50 p-6 flex flex-col items-center justify-center gap-2">
                  <CheckCircle2 className="h-6 w-6 opacity-40 text-slate-400" />
                  <span>No payment drops recovered on this date.</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tank Stock & Credit Parties Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tank Wise Stocks */}
        <Card className="rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Droplet className="h-4 w-4 text-blue-500" />
              Tank Wise Stock Levels (Litres)
              {isTankStockEmpty && <span className="text-[10px] text-slate-400 font-bold bg-slate-100 px-2 py-0.5 rounded ml-2">Empty State Outline</span>}
            </CardTitle>
            <CardDescription className="text-xs text-slate-500 mt-0.5">
              Available liquid fuel stocks in active bunk tanks
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {displayTankStock.length > 0 ? (
              <div className="space-y-3">
                {displayTankStock.map((tank: any, idx: number) => {
                  const pct = tank.capacity > 0 ? Math.min(100, (tank.currentStock / tank.capacity) * 100) : 0;
                  const stockColors = ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#14b8a6", "#f43f5e"];
                  const color = stockColors[idx % stockColors.length];
                  const isLow = pct < 25;
                  return (
                    <div key={idx} className={`p-3 rounded-xl border ${tank.isGhost ? 'opacity-40 border-slate-100 bg-slate-50' : 'border-slate-100 bg-slate-50/60'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: tank.isGhost ? '#cbd5e1' : color }} />
                          <span className="text-xs font-bold text-slate-700">{tank.tankName}</span>
                          {tank.productName && tank.productName !== '-' && (
                            <span className="text-[10px] bg-white border border-slate-200 text-slate-500 font-bold px-1.5 py-0.5 rounded">{tank.productName}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isLow && !tank.isGhost && (
                            <span className="text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded">LOW</span>
                          )}
                          <span className="text-sm font-black" style={{ color: tank.isGhost ? '#94a3b8' : color }}>
                            {tank.currentStock.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L
                          </span>
                          {tank.capacity > 0 && (
                            <span className="text-[11px] text-slate-400 font-semibold">/ {tank.capacity.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} L</span>
                          )}
                        </div>
                      </div>
                      {tank.capacity > 0 && (
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{ width: `${pct}%`, background: tank.isGhost ? '#cbd5e1' : (isLow ? '#f43f5e' : color) }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-12 text-center text-slate-400 text-xs font-medium border border-dashed border-slate-100 rounded-xl bg-slate-50/50 p-6 flex flex-col items-center justify-center gap-2">
                <Droplet className="h-6 w-6 opacity-40 text-slate-400" />
                <span>No tanks configured. Set up tanks in Tank &amp; Nozzle settings.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* All Credit Parties Stacked Chart */}
        <Card className="rounded-2xl shadow-sm border border-slate-200/80 bg-white">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              Monthly Credit vs Recovery Balance
              {isCreditPartiesEmpty && <span className="text-[10px] text-slate-400 font-bold bg-slate-100 px-2 py-0.5 rounded ml-2">Empty State Outline</span>}
            </CardTitle>
            <CardDescription className="text-xs text-slate-500 mt-0.5">
              Monthly comparative balances of credit sales and recoveries over past 6 months
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={displayCreditParties}>
                  <defs>
                    <linearGradient id="barCreditGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="barRecGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.95}/>
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0.4}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                  <YAxis tickLine={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: "#1e293b", color: "#f8fafc", borderRadius: "12px", border: "0", fontSize: "12px" }}
                  />
                  <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: "11px", fontWeight: "600" }} />
                  <Bar dataKey="creditAmount" stackId="a" radius={[4, 4, 0, 0]} name="Credit Amount (₹)">
                    {displayCreditParties.map((entry: any, index: number) => (
                      <Cell
                        key={`cell-credit-${index}`}
                        fill={entry.isGhost ? "#f8fafc" : "url(#barCreditGrad)"}
                        stroke={entry.isGhost ? "#cbd5e1" : undefined}
                        strokeWidth={entry.isGhost ? 1.5 : undefined}
                        strokeDasharray={entry.isGhost ? "3 3" : undefined}
                      />
                    ))}
                  </Bar>
                  <Bar dataKey="recovery" stackId="b" radius={[4, 4, 0, 0]} name="Recovery (₹)">
                    {displayCreditParties.map((entry: any, index: number) => (
                      <Cell
                        key={`cell-rec-${index}`}
                        fill={entry.isGhost ? "#f8fafc" : "url(#barRecGrad)"}
                        stroke={entry.isGhost ? "#cbd5e1" : undefined}
                        strokeWidth={entry.isGhost ? 1.5 : undefined}
                        strokeDasharray={entry.isGhost ? "3 3" : undefined}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

